import { OperationOutcomeError } from "@medplum/core";
import type { OperationOutcomeIssue } from "@medplum/fhirtypes";
import { getErrorStatusCode } from "@/lib/errors/get-error-status-code";

const AUTHORIZATION_STATUS_CODES = [401, 403];

/** Authenticated-but-refused, excluding `login` (which means not authenticated). */
const FORBIDDEN_ISSUE_CODES: OperationOutcomeIssue["code"][] = ["forbidden", "security"];
const FORBIDDEN_STATUS_CODE = 403;

function hasAuthorizationIssueCode(code: OperationOutcomeIssue["code"] | undefined): boolean {
  return code === "login" || code === "forbidden" || code === "security";
}

/**
 * Detects Medplum/FHIR errors caused by missing or insufficient resource access.
 */
export function isMedplumAuthorizationError(error: unknown): boolean {
  if (error instanceof OperationOutcomeError) {
    const hasAuthorizationIssue =
      error.outcome?.issue?.some((issue) => hasAuthorizationIssueCode(issue.code)) ?? false;

    if (hasAuthorizationIssue) {
      return true;
    }
  }

  const statusCode = getErrorStatusCode(error);
  return statusCode !== undefined && AUTHORIZATION_STATUS_CODES.includes(statusCode);
}

/**
 * Detects a rejection where the caller WAS authenticated and the server refused anyway - an
 * AccessPolicy denial.
 *
 * Narrower than {@link isMedplumAuthorizationError} on purpose: that one also matches 401 /
 * `login`, which is "not authenticated at all". Callers that read a rejection as PROOF a policy
 * held must not accept a 401, because an expired or wrong credential produces one for every
 * request - including the ones the policy would have allowed - so the probe ends up grading
 * itself on a failure it caused.
 */
export function isMedplumForbiddenError(error: unknown): boolean {
  if (error instanceof OperationOutcomeError) {
    const hasForbiddenIssue =
      error.outcome?.issue?.some((issue) => FORBIDDEN_ISSUE_CODES.includes(issue.code)) ?? false;

    if (hasForbiddenIssue) {
      return true;
    }
  }

  return getErrorStatusCode(error) === FORBIDDEN_STATUS_CODE;
}
